import { Eye, EyeOff, Pencil, Plus, Settings2, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { EditorRico } from '@/components/editor/EditorRico';
import { aEditor, COLORES_TEXTO } from '@/components/editor/extensiones';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { OrdenableLista } from '@/components/OrdenableLista';
import { cn } from '@/lib/utils';

import { ArchivoUnico, type ValorArchivo } from '../comun/ArchivoUnico';
import { SelectorClase } from '../comun/SelectorClase';
import {
  useBloquesPlus,
  useBloquesPlusMutaciones,
  useEnarmPlus,
  useEnarmPlusMutaciones,
  type BloquePlus,
  type Capitulo,
  type EnarmPlus,
} from './queries';

type Tipo = 'titulo' | 'subtitulo' | 'texto' | 'imagen';
const TIPOS: [Tipo, string][] = [
  ['titulo', 'Título'],
  ['subtitulo', 'Subtítulo'],
  ['texto', 'Texto'],
  ['imagen', 'Imagen'],
];

function ModalManual({ d, onCerrar }: { d: EnarmPlus; onCerrar: () => void }) {
  const { manual } = useEnarmPlusMutaciones();
  const [f, setF] = useState<{
    name: string;
    isPublished: boolean;
    cover: ValorArchivo | null;
    summary: ValorArchivo | null;
  }>({
    name: d.manual.name,
    isPublished: d.manual.isPublished,
    cover: d.manual.cover,
    summary: d.manual.summary,
  });
  return (
    <Modal abierto onCerrar={onCerrar} titulo="Datos del manual" ancho="max-w-2xl">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          manual.mutate(
            {
              name: f.name.trim(),
              isPublished: f.isPublished,
              coverPath: f.cover?.path ?? null,
              summaryPath: f.summary?.path ?? null,
            },
            { onSuccess: onCerrar },
          );
        }}
      >
        <Campo etiqueta="Nombre">
          <input
            className="input-vidrio"
            required
            minLength={2}
            maxLength={500}
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Campo>
        <div className="grid gap-4 sm:grid-cols-2">
          <ArchivoUnico
            destino="enarm-plus"
            tipos={['image']}
            valor={f.cover}
            quitable
            etiqueta="Portada"
            onCambio={(cover) => setF({ ...f, cover })}
          />
          <ArchivoUnico
            destino="enarm-plus"
            tipos={['image']}
            valor={f.summary}
            quitable
            etiqueta="Imagen de resumen"
            onCambio={(summary) => setF({ ...f, summary })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--color-azul-600)]"
            checked={f.isPublished}
            onChange={(e) => setF({ ...f, isPublished: e.target.checked })}
          />
          Publicado
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-cs btn-primario" disabled={manual.isPending}>
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ModalCapitulo({ cap, onCerrar }: { cap: Capitulo | null; onCerrar: () => void }) {
  const { crearCapitulo, editarCapitulo } = useEnarmPlusMutaciones();
  const [f, setF] = useState({
    title: cap?.title ?? '',
    isVisible: cap?.isVisible ?? false,
    tags: cap?.tags.join(', ') ?? '',
  });
  const body = {
    title: f.title.trim(),
    isVisible: f.isVisible,
    tags: f.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
  };
  return (
    <Modal abierto onCerrar={onCerrar} titulo={cap ? 'Editar capítulo' : 'Nuevo capítulo'}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (cap) editarCapitulo.mutate({ id: cap.id, body }, { onSuccess: onCerrar });
          else crearCapitulo.mutate(body, { onSuccess: onCerrar });
        }}
      >
        <Campo etiqueta="Título">
          <input
            className="input-vidrio"
            required
            minLength={2}
            maxLength={150}
            autoFocus
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Etiquetas" ayuda="Separadas por coma.">
          <input
            className="input-vidrio"
            value={f.tags}
            onChange={(e) => setF({ ...f, tags: e.target.value })}
          />
        </Campo>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--color-azul-600)]"
            checked={f.isVisible}
            onChange={(e) => setF({ ...f, isVisible: e.target.checked })}
          />
          Visible para el alumno
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={crearCapitulo.isPending || editarCapitulo.isPending}
          >
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Un bloque como se ve en el libro del alumno. */
function VistaBloque({ b, cdnBase }: { b: BloquePlus; cdnBase: string | null }) {
  const color = b.textColor && COLORES_TEXTO[b.textColor] ? COLORES_TEXTO[b.textColor] : undefined;
  return (
    <div style={{ color }}>
      {b.type === 'titulo' && <h2 className="text-xl font-extrabold">{b.title}</h2>}
      {b.type === 'subtitulo' && <h3 className="text-lg font-bold">{b.subtitle}</h3>}
      {b.type === 'texto' && (
        <div
          className="contenido-libro text-sm"
          // El HTML ya viene limpio de la API; solo se resuelven las imágenes relativas.
          dangerouslySetInnerHTML={{ __html: aEditor(b.html ?? '', cdnBase) }}
        />
      )}
      {b.type === 'imagen' &&
        (b.image?.url ? (
          <img src={b.image.url} alt="" loading="lazy" className="max-h-80 rounded-lg" />
        ) : (
          <p className="text-sm text-texto-3">Imagen no disponible</p>
        ))}
      {b.className && (
        <p className="mt-1 text-xs text-texto-3">
          Clase relacionada:{' '}
          <Link to={`/contenido/clases/${String(b.classId)}`} className="underline">
            {b.className}
          </Link>
        </p>
      )}
    </div>
  );
}

function ModalBloque({
  capitulo,
  bloque,
  afterId,
  cdnBase,
  onCerrar,
}: {
  capitulo: number;
  bloque: BloquePlus | null;
  afterId: number | null;
  cdnBase: string | null;
  onCerrar: () => void;
}) {
  const mut = useBloquesPlusMutaciones(capitulo);
  const [f, setF] = useState({
    type: (bloque?.type ?? 'texto') as Tipo,
    title: bloque?.title ?? '',
    subtitle: bloque?.subtitle ?? '',
    html: bloque?.html ?? '',
    image: bloque?.image ?? null,
    textColor: bloque?.textColor ?? '',
    classId: bloque?.classId ?? null,
  });
  const poner = (c: Partial<typeof f>) => setF((v) => ({ ...v, ...c }));
  const enviar = () => {
    const comun = { textColor: f.textColor || null, classId: f.classId };
    const propio =
      f.type === 'titulo'
        ? { title: f.title.trim() }
        : f.type === 'subtitulo'
          ? { subtitle: f.subtitle.trim() }
          : f.type === 'texto'
            ? { html: f.html }
            : f.image && f.image.path !== bloque?.image?.path
              ? { imagePath: f.image.path }
              : {};
    if (f.type === 'imagen' && !f.image) return void toast.error('Sube la imagen.');
    const listo = { onSuccess: onCerrar };
    if (bloque) mut.editar.mutate({ childId: bloque.id, body: { ...comun, ...propio } }, listo);
    else mut.agregar.mutate({ type: f.type, afterId, ...comun, ...propio }, listo);
  };
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={bloque ? 'Editar bloque' : 'Nuevo bloque'}
      ancho="max-w-4xl"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
      >
        {!bloque && (
          <div role="radiogroup" aria-label="Tipo de bloque" className="flex flex-wrap gap-2">
            {TIPOS.map(([k, t]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={f.type === k}
                className={cn('btn-cs', f.type === k ? 'btn-primario' : 'btn-vidrio')}
                onClick={() => poner({ type: k })}
              >
                {t}
              </button>
            ))}
          </div>
        )}
        {bloque?.legacyFile && (
          <p className="rounded-lg bg-[var(--pista)] p-3 text-sm">
            Este texto todavía está en el archivo «{bloque.legacyFile}» del CDN. Al guardar queda en
            la base de datos.
          </p>
        )}
        {f.type === 'titulo' && (
          <Campo etiqueta="Título">
            <input
              className="input-vidrio"
              required
              maxLength={100}
              value={f.title}
              onChange={(e) => poner({ title: e.target.value })}
            />
          </Campo>
        )}
        {f.type === 'subtitulo' && (
          <Campo etiqueta="Subtítulo">
            <input
              className="input-vidrio"
              required
              maxLength={150}
              value={f.subtitle}
              onChange={(e) => poner({ subtitle: e.target.value })}
            />
          </Campo>
        )}
        {f.type === 'texto' && (
          <EditorRico
            valor={f.html}
            cdnBase={cdnBase}
            destinoImagen="enarm-plus"
            onCambio={(html) => poner({ html })}
          />
        )}
        {f.type === 'imagen' && (
          <ArchivoUnico
            destino="enarm-plus"
            tipos={['image']}
            valor={f.image}
            etiqueta="Imagen"
            onCambio={(image) => poner({ image })}
          />
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {f.type !== 'imagen' && (
            <Campo etiqueta="Color del bloque">
              <select
                className="input-vidrio"
                value={f.textColor}
                onChange={(e) => poner({ textColor: e.target.value })}
              >
                <option value="">Predeterminado</option>
                {Object.keys(COLORES_TEXTO).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </Campo>
          )}
          <div>
            <span className="mb-1.5 block text-sm font-bold text-texto-2">Clase relacionada</span>
            <SelectorClase valor={f.classId} onCambio={(classId) => poner({ classId })} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={mut.agregar.isPending || mut.editar.isPending}
          >
            {bloque ? 'Guardar' : 'Agregar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Bloques({ cap, cdnBase }: { cap: Capitulo; cdnBase: string | null }) {
  const editable = useCan('contenido', 'editar');
  const q = useBloquesPlus(cap.id);
  const mut = useBloquesPlusMutaciones(cap.id);
  const [modal, setModal] = useState<{ bloque: BloquePlus | null; afterId: number | null } | null>(
    null,
  );
  const [quitar, setQuitar] = useState<BloquePlus | null>(null);
  const insertar = (afterId: number | null): ReactNode =>
    editable && (
      <button
        type="button"
        className="flex w-full items-center justify-center gap-1 py-1 text-xs text-texto-2 hover:text-texto"
        onClick={() => setModal({ bloque: null, afterId })}
      >
        <Plus size={12} aria-hidden /> Insertar bloque aquí
      </button>
    );
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-extrabold">
          {cap.title} {!cap.isVisible && <span className="insignia align-middle">Oculto</span>}
        </h2>
        {editable && (
          <button
            type="button"
            className="btn-cs btn-primario"
            onClick={() => setModal({ bloque: null, afterId: null })}
          >
            <Plus size={16} aria-hidden /> Bloque al final
          </button>
        )}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Capítulo sin bloques" />}
      {q.data && (
        <OrdenableLista
          items={q.data}
          idDe={(b) => b.id}
          deshabilitado={!editable || mut.ordenar.isPending}
          onOrdenar={(ids) => mut.ordenar.mutate(ids)}
        >
          {(b, asa) => (
            <div>
              <div className="panel-vidrio flex items-start gap-2 p-3">
                {asa}
                <div className="min-w-0 flex-1">
                  <VistaBloque b={b} cdnBase={cdnBase} />
                </div>
                {editable && (
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      className="btn-cs btn-vidrio px-2 py-1"
                      aria-label="Editar bloque"
                      onClick={() => setModal({ bloque: b, afterId: null })}
                    >
                      <Pencil size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="btn-cs btn-vidrio px-2 py-1 text-rojo-texto"
                      aria-label="Borrar bloque"
                      onClick={() => setQuitar(b)}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </div>
                )}
              </div>
              {insertar(b.id)}
            </div>
          )}
        </OrdenableLista>
      )}
      {modal && (
        <ModalBloque
          capitulo={cap.id}
          bloque={modal.bloque}
          afterId={modal.afterId}
          cdnBase={cdnBase}
          onCerrar={() => setModal(null)}
        />
      )}
      <ConfirmarAccion
        abierto={quitar !== null}
        onCerrar={() => setQuitar(null)}
        titulo="Borrar bloque"
        descripcion="Se quita del capítulo."
        textoBoton="Borrar"
        peligro
        ocupado={mut.quitar.isPending}
        onConfirmar={() =>
          quitar && mut.quitar.mutate(quitar.id, { onSuccess: () => setQuitar(null) })
        }
      />
    </section>
  );
}

function Editor({ d }: { d: EnarmPlus }) {
  const editable = useCan('contenido', 'editar');
  const { editarCapitulo, borrarCapitulo, ordenarCapitulos } = useEnarmPlusMutaciones();
  const [elegido, setElegido] = useState<number | null>(d.chapters[0]?.id ?? null);
  const [modalCap, setModalCap] = useState<{ cap: Capitulo | null } | null>(null);
  const [datos, setDatos] = useState(false);
  const [borrando, setBorrando] = useState<Capitulo | null>(null);
  const { proteger, dialogo } = useStepUp();
  const cap = d.chapters.find((c) => c.id === elegido) ?? null;
  return (
    <>
      <Encabezado
        titulo={d.manual.name}
        descripcion={`Manual ENARM Plus · ${d.chapters.length} capítulos · ${d.manual.isPublished ? 'publicado' : 'sin publicar'}. El alumno solo ve los capítulos visibles.`}
        acciones={
          editable && (
            <button type="button" className="btn-cs btn-vidrio" onClick={() => setDatos(true)}>
              <Settings2 size={16} aria-hidden /> Datos del manual
            </button>
          )
        }
      />
      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <aside className="panel-vidrio h-fit space-y-2 p-3 lg:sticky lg:top-4">
          <div className="flex items-center justify-between">
            <h2 className="font-extrabold">Capítulos</h2>
            {editable && (
              <button
                type="button"
                className="btn-cs btn-vidrio px-2 py-1"
                aria-label="Nuevo capítulo"
                onClick={() => setModalCap({ cap: null })}
              >
                <Plus size={14} aria-hidden />
              </button>
            )}
          </div>
          {d.chapters.length === 0 && <Vacio titulo="Sin capítulos" />}
          <OrdenableLista
            items={d.chapters}
            idDe={(c) => c.id}
            deshabilitado={!editable || ordenarCapitulos.isPending}
            className="max-h-[70vh] space-y-1 overflow-y-auto"
            onOrdenar={(ids) => ordenarCapitulos.mutate(ids)}
          >
            {(c, asa) => (
              <div
                className={cn(
                  'flex items-center gap-1 rounded-lg px-1 py-1 text-sm',
                  c.id === elegido && 'bg-[var(--pista)]',
                )}
              >
                {asa}
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left font-semibold"
                  onClick={() => setElegido(c.id)}
                  title={c.title}
                >
                  {c.title}
                  <span className="ml-1 text-xs font-normal text-texto-3">({c.blocksCount})</span>
                </button>
                {editable && (
                  <>
                    <button
                      type="button"
                      className="text-texto-3 hover:text-texto"
                      aria-label={c.isVisible ? `Ocultar ${c.title}` : `Mostrar ${c.title}`}
                      title={
                        c.isVisible ? 'Visible (clic para ocultar)' : 'Oculto (clic para mostrar)'
                      }
                      onClick={() =>
                        editarCapitulo.mutate({ id: c.id, body: { isVisible: !c.isVisible } })
                      }
                    >
                      {c.isVisible ? (
                        <Eye size={14} aria-hidden />
                      ) : (
                        <EyeOff size={14} aria-hidden />
                      )}
                    </button>
                    <button
                      type="button"
                      className="text-texto-3 hover:text-texto"
                      aria-label={`Editar ${c.title}`}
                      onClick={() => setModalCap({ cap: c })}
                    >
                      <Pencil size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="text-texto-3 hover:text-rojo-texto"
                      aria-label={`Borrar ${c.title}`}
                      onClick={() => setBorrando(c)}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )}
          </OrdenableLista>
        </aside>
        <div>
          {cap ? (
            <Bloques cap={cap} cdnBase={d.cdnBaseUrl} />
          ) : (
            <Vacio titulo="Elige un capítulo" />
          )}
        </div>
      </div>
      {modalCap && <ModalCapitulo cap={modalCap.cap} onCerrar={() => setModalCap(null)} />}
      {datos && <ModalManual d={d} onCerrar={() => setDatos(false)} />}
      <ConfirmarAccion
        abierto={borrando !== null}
        onCerrar={() => setBorrando(null)}
        titulo={`Borrar «${borrando?.title ?? ''}»`}
        descripcion={
          borrando && borrando.blocksCount > 0
            ? `El capítulo tiene ${borrando.blocksCount} bloque(s); se borran todos.`
            : 'El capítulo no tiene bloques.'
        }
        textoBoton="Borrar"
        peligro
        ocupado={borrarCapitulo.isPending}
        onConfirmar={() =>
          borrando &&
          void proteger(() => borrarCapitulo.mutateAsync(borrando.id))
            .then(() => {
              toast.success('Capítulo borrado.');
              if (elegido === borrando.id) setElegido(null);
              setBorrando(null);
            })
            .catch((e: unknown) => toast.error(mensajeDe(e)))
        }
      />
      {dialogo}
    </>
  );
}

export function EnarmPlusPage() {
  const q = useEnarmPlus();
  return (
    <PermissionGate seccion="contenido">
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && <Editor d={q.data} />}
    </PermissionGate>
  );
}
